import { LIVE_DATA_SOURCE_LABEL } from './journeyApiFilters';

export function mapRevenueMovementToView(api, source = 'api') {
  const series = api.series || [];
  const totals = api.totals || { cmp: 0, pos: 0, app: 0 };
  const hasAny =
    Number(totals.cmp || 0) + Number(totals.pos || 0) + Number(totals.app || 0) > 0 ||
    series.some((row) => Number(row.cmp || 0) + Number(row.pos || 0) + Number(row.app || 0) > 0);

  return {
    dataSourceLabel: source === 'api' ? LIVE_DATA_SOURCE_LABEL : 'Demo · mock',
    currencyCode: api.currencyCode || totals.currencyCode || 'DKK',
    totals: {
      cmp: Number(totals.cmp || 0),
      pos: Number(totals.pos || 0),
      app: Number(totals.app || 0),
    },
    series: series.map((row) => ({
      date: row.date,
      cmp: Number(row.cmp || 0),
      pos: Number(row.pos || 0),
      app: Number(row.app || 0),
    })),
    empty: !hasAny,
    notes: [...(api.notes || []), ...(api.dataGaps || [])],
    raw: api,
  };
}

export function mapScanHeatmapToView(api, source = 'api') {
  const { hours, gates } = normalizeScanHeatmapGates(api);
  const totalScans = Number(api.totalScans || 0);
  const maxCell = Math.max(
    0,
    ...gates.flatMap((gate) => (gate.byHour || []).map((n) => Number(n || 0)))
  );
  const notes = [...(api.notes || []), ...(api.dataGaps || [])];
  const empty = totalScans === 0 || gates.length === 0;
  const onlyUnassigned =
    gates.length > 0 && gates.every((g) => (g.gateId || g.gateName) === 'unassigned');

  return {
    dataSourceLabel: source === 'api' ? LIVE_DATA_SOURCE_LABEL : 'Demo · mock',
    hours,
    gates: gates.map((gate) => ({
      ...gate,
      byHour: (gate.byHour || hours.map(() => 0)).map((n) => Number(n || 0)),
    })),
    totalScans,
    maxCell,
    empty,
    notes,
    confidence: {
      tone: empty || onlyUnassigned ? 'warning' : 'success',
      title: empty
        ? 'ACP scan data is missing or empty — congestion cannot be measured yet.'
        : onlyUnassigned
          ? 'Scans are flowing, but gate/zone is still unassigned in ACP payloads.'
          : 'Scan volume by gate and hour of day.',
    },
    raw: api,
  };
}

/** Accept FE mock shape ({ gates, hours }) or live shapes (gates×byHour, or legacy buckets). */
function normalizeScanHeatmapGates(api) {
  const hours = normalizeHeatmapHourAxis(api.hours);

  if (Array.isArray(api.gates) && api.gates.length > 0) {
    return {
      hours,
      gates: api.gates.map((gate) => ({
        ...gate,
        byHour: padByHour(gate.byHour, hours.length),
      })),
    };
  }

  const buckets = Array.isArray(api.buckets) ? api.buckets : [];
  const byZone = new Map();

  for (const bucket of buckets) {
    const zone = String(bucket.zone || 'unassigned');
    if (!byZone.has(zone)) {
      byZone.set(zone, hours.map(() => 0));
    }
    const hour = hourFromBucketStart(bucket.bucketStart);
    if (hour == null || hour < 0 || hour > 23) continue;
    byZone.get(zone)[hour] += Number(bucket.count || 0);
  }

  const gates = [...byZone.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([zone, byHour]) => ({
      gateId: zone,
      gateName: zone === 'unassigned' ? 'Unassigned' : zone,
      zoneId: zone,
      zoneName: zone,
      byHour,
    }));

  return { hours, gates };
}

/**
 * Board indexes byHour[hour] where hour is 0..23.
 * Some API responses wrongly put per-hour totals in `hours` — treat those as non-axis data.
 */
function normalizeHeatmapHourAxis(rawHours) {
  const fallback = Array.from({ length: 24 }, (_, h) => h);
  if (!Array.isArray(rawHours) || rawHours.length === 0) return fallback;
  const looksLikeIndices = rawHours.every((h, i) => Number(h) === i);
  if (looksLikeIndices) return rawHours.map((h) => Number(h));
  // Length-24 numeric arrays that aren't 0..23 are hour totals, not labels.
  if (rawHours.length === 24 && rawHours.every((h) => Number.isFinite(Number(h)))) {
    return fallback;
  }
  return rawHours.map((h) => Number(h)).filter((h) => Number.isFinite(h));
}

function padByHour(byHour, length) {
  const values = Array.isArray(byHour) ? byHour.map((n) => Number(n || 0)) : [];
  if (values.length >= length) return values.slice(0, length);
  return [...values, ...Array.from({ length: length - values.length }, () => 0)];
}

function hourFromBucketStart(bucketStart) {
  if (!bucketStart) return null;
  if (bucketStart instanceof Date) return bucketStart.getUTCHours();
  const raw = String(bucketStart);
  // Prefer explicit time portion so timezone offsets in ISO strings don't shift the hour label.
  const match = raw.match(/T(\d{2}):/);
  if (match) return Number(match[1]);
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getUTCHours();
}

export function mapDataFreshnessToView(api, source = 'api') {
  const sources = (api.sources || []).map((item) => ({
    source: item.source,
    status: item.status || 'empty',
    lastEventAt: item.lastEventAt || null,
    lagSeconds: item.lagSeconds == null ? null : Number(item.lagSeconds),
    eventCount24h: Number(item.eventCount24h || 0),
    label: formatFreshnessLabel(item),
  }));

  const worst = sources.some((s) => s.status === 'error')
    ? 'error'
    : sources.some((s) => s.status === 'empty')
      ? 'empty'
      : sources.some((s) => s.status === 'stale')
        ? 'stale'
        : 'fresh';

  return {
    source,
    checkedAt: api.checkedAt || null,
    sources,
    worst,
    raw: api,
  };
}

function formatFreshnessLabel(item) {
  if (item.status === 'empty') return `${item.source} missing data`;
  if (item.status === 'error') return `${item.source} ingest error`;
  if (item.status === 'stale') {
    const hours = item.lagSeconds != null ? Math.round(item.lagSeconds / 3600) : null;
    return hours != null && hours >= 1
      ? `${item.source} delayed · ${hours}h`
      : `${item.source} delayed`;
  }
  if (item.lastEventAt) {
    const mins = Math.max(1, Math.round((Date.now() - new Date(item.lastEventAt).getTime()) / 60000));
    if (mins < 60) return `${item.source} updated ${mins}m ago`;
    return `${item.source} updated ${Math.round(mins / 60)}h ago`;
  }
  return `${item.source} fresh`;
}
